import { useRef, useState } from "react";
import type { SupportAttachment } from "@victor/api-client";
import { api } from "../../lib/api";

/** Télécharge une pièce jointe (fetch authentifié) puis l'ouvre. */
export async function downloadAndOpen(key: string, filename: string) {
  try {
    const blob = await api.support.downloadAttachment(key);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  } catch {
    // ignore
  }
}

export function useAttachments() {
  const [pending, setPending] = useState<SupportAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const add = async (file: File) => {
    setBusy(true);
    try {
      const a = await api.support.uploadAttachment(file);
      setPending((p) => [...p, a]);
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };
  return {
    pending,
    busy,
    add,
    remove: (key: string) => setPending((p) => p.filter((a) => a.key !== key)),
    clear: () => setPending([]),
  };
}

const Paperclip = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

export function AttachButton({
  onFile,
  busy,
}: {
  onFile: (f: File) => void;
  busy: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        aria-label="Joindre un fichier"
        title="Joindre un fichier"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-navy-500 ring-1 ring-navy-200 transition-colors hover:bg-navy-50 hover:text-navy-800 disabled:opacity-50"
      >
        {busy ? "…" : Paperclip}
      </button>
      <input
        ref={ref}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </>
  );
}

export function PendingChips({
  items,
  onRemove,
}: {
  items: SupportAttachment[];
  onRemove: (key: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {items.map((a) => (
        <span
          key={a.key}
          className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-2.5 py-1 text-xs text-navy-700 ring-1 ring-navy-100"
        >
          📎 {a.filename}
          <button
            type="button"
            onClick={() => onRemove(a.key)}
            className="text-navy-400 hover:text-red-500"
            aria-label="Retirer"
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  );
}

export function MsgAttachments({
  items,
  mine,
}: {
  items: SupportAttachment[];
  mine?: boolean;
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((a) => (
        <button
          key={a.key}
          type="button"
          onClick={() => downloadAndOpen(a.key, a.filename)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ring-1 transition-colors ${
            mine
              ? "bg-white/15 text-white ring-white/20 hover:bg-white/25"
              : "bg-white text-navy-700 ring-navy-100 hover:bg-navy-50"
          }`}
        >
          📎 {a.filename}
        </button>
      ))}
    </div>
  );
}
